/**
 * N194 — Configuração Armazenamento
 *
 * Onde as fotos e anexos são guardados, quanto ocupam, por quanto tempo são
 * mantidos, e o caminho que uma ferramenta de backup incremental deve ler.
 *
 * ⛔ CABE EM UMA TELA, SEM ROLAR — é requisito, não estética.
 * A versão anterior empilhava quatro blocos e obrigava a rolar para comparar a
 * configuração com o efeito dela. Aqui é duas colunas: decisão à esquerda,
 * consequência à direita, lado a lado. Ao mexer nesta tela, meça a altura antes
 * de acrescentar bloco novo.
 *
 * ⛔ TRÊS NÚMEROS DIFERENTES, E A PRIMEIRA VERSÃO OS MISTURAVA
 *   1. quanto do HD do servidor está ocupado   (contexto)
 *   2. quanto está reservado para o sistema    (a configuração)
 *   3. quanto do reservado já foi atingido     (o que pede ação)
 * Antes havia um "% de atingimento" solto ao lado de um cartão dizendo "Usado
 * pelas fotos" que exibia o tamanho da PASTA INTEIRA — 646 MB em produção, dos
 * quais só 83 MB são foto. O dono leu e perguntou se tinha 620 MB de fotos: a
 * tela ensinou o erro.
 *
 * ⛔ O CAMINHO-RAIZ É SOMENTE LEITURA. Vem do bind-mount do compose; editá-lo
 * pela tela permitiria apontar para um path não montado e perder os arquivos no
 * próximo recreate do container.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  Cloud,
  Copy,
  HardDrive,
  Loader2,
  RefreshCw,
  Save,
} from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../../services/apiClient';
import PermissionGate, { usePermissoesTela } from '../PermissionGate';

interface ItemReparticao {
  grupo: string;
  rotulo: string;
  arquivos: number;
  bytes: number;
  gb: number;
}

interface StatusArmazenamento {
  modo_configurado: string;
  modo_ativo: string;
  azure_disponivel: boolean;
  disco_total_gb: number;
  disco_livre_gb: number;
  disco_usado_gb: number;
  percentual_disco: number | null;
  quota_gb: number | null;
  usado_disco_gb: number;
  percentual_quota: number | null;
  reparticao: ItemReparticao[];
  fotos_no_banco: number;
  tamanho_no_banco_gb: number;
  caminho_container: string;
  caminho_host: string;
  estrutura_pastas: string;
  ambiente: string;
}

interface ConfigArmazenamento {
  storage_mode: string;
  quota_fotos_gb: number | null;
  retencao_fotos_meses: number | null;
  retencao_fotos_ativa: boolean;
}

const CARTAO =
  'bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-5';
const CAMPO =
  'px-3 py-1.5 border-2 border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 tabular-nums disabled:opacity-60';
const TITULO =
  'text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3';

/** Verde < 70, âmbar < 90, vermelho >= 90. Alerta que grita sempre é ignorado. */
const faixa = (pct: number | null) => {
  if (pct === null) return { barra: 'bg-gray-300 dark:bg-gray-600', texto: 'text-gray-500' };
  if (pct >= 90) return { barra: 'bg-red-500', texto: 'text-red-600 dark:text-red-400' };
  if (pct >= 70) return { barra: 'bg-amber-500', texto: 'text-amber-600 dark:text-amber-400' };
  return { barra: 'bg-emerald-500', texto: 'text-emerald-600 dark:text-emerald-400' };
};

/** GB abaixo de 1 vira MB: "0.06 GB" não diz nada a ninguém. */
const tamanho = (gb: number): string =>
  gb >= 1
    ? `${gb.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} GB`
    : `${Math.round(gb * 1024).toLocaleString('pt-BR')} MB`;

const Barra: React.FC<{
  titulo: string;
  detalhe: string;
  pct: number | null;
  semLimite?: string;
}> = ({ titulo, detalhe, pct, semLimite }) => {
  const cor = faixa(pct);
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{titulo}</span>
        {/* O percentual fica À DIREITA — foi o pedido explícito. */}
        <span className={`text-lg font-bold tabular-nums ${cor.texto}`}>
          {pct === null ? '—' : `${pct.toLocaleString('pt-BR')}%`}
        </span>
      </div>
      <div
        className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mt-1.5"
        role="progressbar"
        aria-valuenow={pct ?? 0}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={titulo}
      >
        <div
          className={`h-full rounded-full transition-[width] duration-200 ${cor.barra}`}
          style={{ width: `${Math.min(Math.max(pct ?? 0, 0), 100)}%` }}
        />
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 tabular-nums">
        {pct === null ? semLimite : detalhe}
      </p>
    </div>
  );
};

export const ArmazenamentoSection: React.FC = () => {
  const [status, setStatus] = useState<StatusArmazenamento | null>(null);
  const [config, setConfig] = useState<ConfigArmazenamento | null>(null);
  const [original, setOriginal] = useState<ConfigArmazenamento | null>(null);
  const [preview, setPreview] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [copiado, setCopiado] = useState(false);

  const { podeEditar } = usePermissoesTela('CONFIGURACOES_VIEW');
  const somenteLeitura = !podeEditar;

  const carregar = useCallback(async () => {
    setErro(false);
    try {
      const [s, c] = await Promise.all([
        apiClient.get('/config/armazenamento/status'),
        apiClient.get('/config/armazenamento'),
      ]);
      setStatus(s.data);
      setConfig(c.data);
      setOriginal(c.data);
    } catch {
      // Antes isto virava um toast e a tela ficava vazia para sempre, sem dizer
      // o que houve nem oferecer nova tentativa.
      setErro(true);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  // O preview é o que torna a decisão concreta: sem ele o MASTER liga a
  // retenção sem saber quantas fotos vai perder.
  useEffect(() => {
    if (!config?.retencao_fotos_meses) { setPreview(''); return; }
    const t = setTimeout(async () => {
      try {
        const r = await apiClient.get('/config/armazenamento/retencao-preview', {
          params: { meses: config.retencao_fotos_meses },
        });
        setPreview(r.data.mensagem);
      } catch { setPreview(''); }
    }, 400);
    return () => clearTimeout(t);
  }, [config?.retencao_fotos_meses]);

  const alterado = useMemo(
    () => JSON.stringify(config) !== JSON.stringify(original),
    [config, original],
  );

  const salvar = async () => {
    if (!config) return;
    setSalvando(true);
    try {
      await apiClient.post('/config/armazenamento', config);
      toast.success('Configuração salva');
      await carregar();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Não consegui salvar');
    } finally {
      setSalvando(false);
    }
  };

  const copiar = async () => {
    if (!status?.caminho_host) return;
    try {
      await navigator.clipboard.writeText(status.caminho_host);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      toast.error('Não consegui copiar');
    }
  };

  if (carregando) {
    return (
      <div className="flex items-center justify-center p-16 text-gray-500">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
        Carregando armazenamento...
      </div>
    );
  }

  if (erro || !status || !config) {
    return (
      <div className={`${CARTAO} text-center`}>
        <AlertTriangle className="w-8 h-8 mx-auto text-amber-500 mb-3" />
        <p className="text-gray-700 dark:text-gray-300 font-medium">
          Não consegui carregar a configuração de armazenamento.
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          O servidor pode estar reiniciando.
        </p>
        <button
          onClick={carregar}
          className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar de novo
        </button>
      </div>
    );
  }

  const maior = status.reparticao[0]?.bytes || 1;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Configuração Armazenamento
        </h2>
        <div className="flex items-center gap-3">
          {alterado && !salvando && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              alterações não salvas
            </span>
          )}
          <PermissionGate tela="CONFIGURACOES_VIEW" acao="editar">
            <button
              onClick={salvar}
              disabled={salvando || !alterado}
              className="inline-flex items-center bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-5 py-2 rounded-lg font-medium transition-colors gap-2 text-sm"
            >
              {salvando
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <Save className="w-4 h-4" />}
              Salvar
            </button>
          </PermissionGate>
          <button
            onClick={carregar}
            title="Atualizar números"
            className="p-2 rounded-lg text-gray-500 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Duas colunas: decisão à esquerda, consequência à direita — é o que
          permite comparar sem rolar. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">

        {/* ══════ CONFIGURAÇÃO ══════ */}
        <section className={CARTAO}>
          <h3 className={TITULO}>Configuração</h3>

          {/* Onde gravar vem PRIMEIRO — é a decisão que governa todo o resto. */}
          <label
            htmlFor="modo-storage"
            className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"
          >
            Onde as fotos são gravadas
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              id="modo-storage"
              value={config.storage_mode}
              disabled={somenteLeitura}
              onChange={(e) => setConfig({ ...config, storage_mode: e.target.value })}
              className={`${CAMPO} flex-1 min-w-[12rem]`}
            >
              <option value="auto">Automático</option>
              <option value="local">Disco local</option>
              <option value="azure" disabled={!status.azure_disponivel}>
                Azure Blob Storage
              </option>
            </select>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 whitespace-nowrap">
              {status.modo_ativo === 'local'
                ? <><HardDrive className="w-3.5 h-3.5" /> em disco</>
                : <><Cloud className="w-3.5 h-3.5" /> no Azure</>}
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            {status.azure_disponivel
              ? 'A leitura procura nos dois lugares — trocar não faz arquivo existente sumir.'
              : 'Sem credencial do Azure neste ambiente.'}
          </p>

          <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t-2 border-gray-100 dark:border-gray-700">
            <div>
              <label
                htmlFor="quota"
                className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"
              >
                Espaço reservado
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="quota"
                  type="number"
                  min={1}
                  disabled={somenteLeitura}
                  value={config.quota_fotos_gb ?? ''}
                  onChange={(e) => setConfig({
                    ...config,
                    quota_fotos_gb: e.target.value ? Number(e.target.value) : null,
                  })}
                  placeholder="s/ limite"
                  className={`${CAMPO} w-24`}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">GB</span>
              </div>
            </div>

            <div>
              <label
                htmlFor="retencao"
                className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"
              >
                Manter fotos por
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="retencao"
                  type="number"
                  min={1}
                  disabled={somenteLeitura}
                  value={config.retencao_fotos_meses ?? ''}
                  onChange={(e) => setConfig({
                    ...config,
                    retencao_fotos_meses: e.target.value ? Number(e.target.value) : null,
                  })}
                  placeholder="sempre"
                  className={`${CAMPO} w-24`}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">meses</span>
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 mt-3 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              disabled={somenteLeitura}
              checked={config.retencao_fotos_ativa}
              onChange={(e) => setConfig({ ...config, retencao_fotos_ativa: e.target.checked })}
              className="w-4 h-4 rounded"
            />
            Apagar automaticamente as mais antigas
          </label>

          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            A reserva só calcula o atingimento ao lado. Nunca bloqueia o envio de
            fotos pelos auditores.
          </p>

          {preview && (
            <p className="text-xs mt-3 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200">
              {preview}
            </p>
          )}
        </section>

        {/* ══════ OCUPAÇÃO + REPARTIÇÃO ══════ */}
        <section className={CARTAO}>
          <h3 className={TITULO}>Ocupação</h3>

          <div className="space-y-4">
            {/* O reservado vem primeiro: é o único que leva a uma ação. */}
            <Barra
              titulo="Espaço reservado"
              pct={status.percentual_quota}
              detalhe={`${tamanho(status.usado_disco_gb)} de ${status.quota_gb} GB reservados`}
              semLimite={`${tamanho(status.usado_disco_gb)} usados — sem limite definido`}
            />
            <Barra
              titulo="HD do servidor"
              pct={status.percentual_disco}
              detalhe={`${tamanho(status.disco_usado_gb)} de ${tamanho(status.disco_total_gb)} · ${tamanho(status.disco_livre_gb)} livres`}
              semLimite="não consegui ler o disco"
            />
          </div>

          {status.percentual_quota !== null && status.percentual_quota >= 90 && (
            <p className="flex items-start gap-2 mt-3 p-2.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 text-xs">
              <AlertTriangle className="w-4 h-4 mt-px flex-shrink-0" />
              <span>
                Reserva em {status.percentual_quota}%. <strong>Não bloqueia</strong> o
                envio dos auditores — aumente a reserva ou encurte o período.
              </span>
            </p>
          )}

          {status.reparticao.length > 0 && (
            <div className="mt-4 pt-4 border-t-2 border-gray-100 dark:border-gray-700">
              <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                O que ocupa espaço
              </h4>
              <table className="w-full text-xs">
                <tbody>
                  {status.reparticao.map((item) => (
                    <tr key={item.grupo}>
                      <td className="py-1 pr-2 text-gray-700 dark:text-gray-300">
                        {item.rotulo}
                      </td>
                      <td className="py-1 px-2 text-right tabular-nums text-gray-500 dark:text-gray-400 w-16">
                        {item.arquivos.toLocaleString('pt-BR')}
                      </td>
                      <td className="py-1 px-2 text-right tabular-nums font-semibold text-gray-900 dark:text-gray-100 w-20">
                        {tamanho(item.gb)}
                      </td>
                      <td className="py-1 pl-1 w-16">
                        <div className="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full"
                            style={{ width: `${(item.bytes / maior) * 100}%` }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* ══════ CAMINHOS — faixa compacta, não bloco ══════ */}
      <section className={`${CARTAO} !py-3`}>
        <div className="flex items-center gap-x-6 gap-y-2 flex-wrap text-xs">
          <span className="font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Caminhos ({status.ambiente})
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            aplicação:{' '}
            <span className="font-mono text-gray-800 dark:text-gray-200">
              {status.caminho_container}
            </span>
          </span>
          <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
            backup:{' '}
            {status.caminho_host ? (
              <>
                <span className="font-mono text-gray-800 dark:text-gray-200">
                  {status.caminho_host}
                </span>
                <button
                  onClick={copiar}
                  title="Copiar caminho"
                  className="p-1 rounded text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {copiado
                    ? <Check className="w-3.5 h-3.5 text-emerald-600" />
                    : <Copy className="w-3.5 h-3.5" />}
                </button>
              </>
            ) : (
              <span className="text-amber-600 dark:text-amber-400">
                defina MEDIA_HOST_PATH
              </span>
            )}
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            pastas:{' '}
            <span className="font-mono text-gray-800 dark:text-gray-200">
              {status.estrutura_pastas}
            </span>
          </span>
        </div>
      </section>
    </div>
  );
};
